import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { ChevronDown } from 'lucide-react';
import axios from 'axios';
import { titleize, underscored } from 'underscore.string';
import { withTranslation } from 'react-i18next';
import { FilterCardHeader } from 'components/design-system/molecules/FilterCardHeader';
import { FilterBarRegistry } from 'components/design-system/molecules/FilterBarRegistry';
import { TableSelectionAlert } from 'components/design-system/molecules/TableSelectionAlert';
import { useFilterState, initialValuesFromConfigs, } from 'components/design-system/hooks/useFilterState';
import { buildQuery } from 'components/design-system/utils/buildQuery';
import { getDefaultDateRangeFilter } from 'helpers/date-range';
const DEFAULT_DATE_RANGE_VALUES = {
    options: undefined,
    isDashboard: undefined,
    startValue: 2,
    startTimeUnit: 'month',
    endValue: undefined,
    endTimeUnit: undefined,
    startKey: { key: 'created_at' },
};
import { Button as UIButton, Card, CardContent, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, } from '@cargomatic/ui';
import ExceptionStore from 'stores/ExceptionStore';
import SessionStore from 'stores/SessionStore';
import AccessorialExceptionsTable from './AccessorialExceptionsTable';
import AccessorialsConfirmationModal from 'components/modules/shipments/accessorials/ConfirmationModal';
import AccessorialRequestActionCreators from 'actions/AccessorialRequestActionCreators';
import AccessorialRequestPane from 'components/modules/shipments/AccessorialRequestPane';
import Container from 'components/v2/container';
import CargomaticModal from 'components/v2/modal';
import CargomaticPane from 'components/v2/pane';
import ShipmentModule from 'components/modules/shipments/ShipmentDetailsContainer';
import { getAccessorials } from 'utils/AccessorialWebAPIUtils';
import { getDeclinedAccessorialsReasonCodes } from 'utils/ReasonCodesAPI';
import ToastStore, { showToast } from 'stores/ToastStore';
import { downloadAccessorialExceptionsCSV } from 'utils/AccessorialRequestWebAPIUtils';
import { checkProcessedAccessorial, checkIfAccessorialsNeedsReview, checkIfEquipmentIsMissing, } from 'helpers/accessorials';
import { ExportAccessorialExceptionCSV } from '../ExportAccessorialExceptionCSV';
import BulkAddAccessorialsModal from '../../shipments/accounting/BulkAddAccessorialsModal';
import CargomaticAlert from 'components/ui/Alert';
import { makeAccessorialExceptionFilters } from './AccessorialExceptions.filters';
/**
 * Accessorial Exceptions — plug-and-play FC.
 *
 * Filter state lives entirely in `useFilterState` driven by the declarative
 * `makeAccessorialExceptionFilters({ accessorials, reasonCodes })` config.
 * `buildQuery(FILTERS, values, extras)` produces the API payload. No
 * per-filter handlers, no chip/render switches, no sidecar selectedX state.
 *
 * Non-filter responsibilities (selection, bulk action modals, shipment
 * detail pane, CSV export, polling control) remain in component-local state.
 */
function AccessorialExceptionsBase({ t }) {
    // --- async-loaded option data ------------------------------------------
    const [accessorials, setAccessorials] = useState([]);
    const [reasonCodes, setReasonCodes] = useState([]);
    // --- filter state via design-system hook -------------------------------
    const FILTERS = useMemo(() => makeAccessorialExceptionFilters({ accessorials, reasonCodes }), [accessorials, reasonCodes]);
    const initialValues = useMemo(() => initialValuesFromConfigs(FILTERS), [FILTERS]);
    const { values, displayed, labels, setValue, setMany, setLabel, toggle } = useFilterState(initialValues);
    // --- pagination + sort -------------------------------------------------
    const [activePage, setActivePage] = useState(1);
    const [perPage, setPerPage] = useState(25);
    const [sortOrder, setSortOrder] = useState();
    const [pages, setPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    // --- result + loading + cancellation -----------------------------------
    const [exceptions, setExceptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const cancelRef = useRef(null);
    const shouldPollRef = useRef(true);
    // --- selection ---------------------------------------------------------
    const [selectedIds, setSelectedIds] = useState([]);
    // --- bulk action / modals ----------------------------------------------
    const [action, setAction] = useState('');
    const [openBulkAssignModal, setOpenBulkAssignModal] = useState(false);
    const [exportCSV, setExportCSV] = useState(true);
    // --- detail pane / shipment modal --------------------------------------
    const [paneAccessorialId, setPaneAccessorialId] = useState(null);
    const [paneShipmentId, setPaneShipmentId] = useState(null);
    const [shipmentModalId, setShipmentModalId] = useState(null);
    // --- session user ------------------------------------------------------
    const [user, setUser] = useState(() => SessionStore.getUser());
    useEffect(() => {
        setUser(SessionStore.getUser());
    }, []);
    // -----------------------------------------------------------------------
    // Query construction (config-driven via buildQuery)
    // -----------------------------------------------------------------------
    // Stash FILTERS in a ref so buildAccessorialQuery doesn't re-create when
    // accessorials/reasonCodes load (which would trigger refetch storms).
    const filtersRef = useRef(FILTERS);
    useEffect(() => {
        filtersRef.current = FILTERS;
    }, [FILTERS]);
    const buildAccessorialQuery = useCallback((extras = {}) => {
        const q = buildQuery(filtersRef.current, values, Object.assign(Object.assign({ paginate: true, page: activePage, per_page: perPage }, (sortOrder
            ? { sort: 'approval_due_date', sort_dir: sortOrder }
            : {})), extras));
        // date_range is always required by the API. When the user hasn't picked
        // a concrete range, fall back to the relative default ("last 2 months",
        // keyed by created_at) — matches v1 AccessorialExceptions initial state.
        if (q.date_range == null) {
            q.date_range = getDefaultDateRangeFilter(DEFAULT_DATE_RANGE_VALUES);
        }
        return q;
    }, [values, activePage, perPage, sortOrder]);
    // -----------------------------------------------------------------------
    // Fetch + ExceptionStore subscription
    // -----------------------------------------------------------------------
    const updateAccessorialsView = useCallback(() => {
        if (cancelRef.current)
            cancelRef.current.cancel();
        if (!shouldPollRef.current)
            return;
        const source = axios.CancelToken.source();
        cancelRef.current = source;
        setExceptions([]);
        setLoading(true);
        AccessorialRequestActionCreators.getAccessorialRequests(buildAccessorialQuery(), { cancelToken: source.token });
    }, [buildAccessorialQuery]);
    // ExceptionStore listener: store push → setState
    useEffect(() => {
        const onChange = () => {
            const result = ExceptionStore.getAllAccessorialPaginatedRequests();
            const items = result.items.map((it) => (Object.assign(Object.assign({}, it), { showShipmentModal: (id) => {
                    shouldPollRef.current = false;
                    setShipmentModalId(id);
                }, showAccessorialRequestPane: (acc) => {
                    var _a;
                    const shipmentId = typeof acc.shipment === 'string' ? acc.shipment : (_a = acc.shipment) === null || _a === void 0 ? void 0 : _a._id;
                    setPaneAccessorialId(acc._id);
                    setPaneShipmentId(shipmentId !== null && shipmentId !== void 0 ? shipmentId : null);
                } })));
            setExceptions(items);
            setSelectedIds([]);
            setPages(result.totalPages);
            setTotalCount(result.totalCount);
            setLoading(false);
            cancelRef.current = null;
        };
        const onUpdate = () => updateAccessorialsView();
        ExceptionStore.addChangeListener(onChange);
        ExceptionStore.addAccessorialRequestChangeListener(onUpdate);
        return () => {
            ExceptionStore.removeChangeListener(onChange);
            ExceptionStore.removeAccessorialRequestChangeListener(onUpdate);
        };
    }, [updateAccessorialsView]);
    // Refetch when query inputs change
    useEffect(() => {
        updateAccessorialsView();
        return () => {
            if (cancelRef.current)
                cancelRef.current.cancel();
        };
    }, [updateAccessorialsView]);
    // Page title
    useEffect(() => {
        document.title = 'Cargomatic Inc. - Accessorial Exceptions';
    }, []);
    // One-time async loads for filter option data
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const result = await getAccessorials({ paginate: false });
                const items = result.entity.items.map((a) => ({
                    _id: a._id,
                    name: a.name,
                    slug: a.slug || underscored(a.name.toLowerCase()),
                }));
                if (!cancelled)
                    setAccessorials(items);
            }
            catch (_a) {
                ToastStore.addNotification({
                    title: t('LOADING_ACCESSORIALS_ERROR'),
                    body: '',
                    level: 'error',
                });
            }
        })();
        (async () => {
            try {
                const codes = await getDeclinedAccessorialsReasonCodes();
                if (!cancelled)
                    setReasonCodes(codes);
            }
            catch (err) {
                console.log('Error during get decline reason codes: ', err);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [t]);
    // -----------------------------------------------------------------------
    // Selection
    // -----------------------------------------------------------------------
    const handleRowSelect = useCallback((row, checked) => {
        const next = checked
            ? [...selectedIds, row._id]
            : selectedIds.filter((id) => id !== row._id);
        const selectedExceptions = exceptions.filter((s) => next.includes(s._id));
        const unprocessed = selectedExceptions.filter((acc) => !checkProcessedAccessorial(acc));
        setSelectedIds(unprocessed.map((acc) => acc._id));
    }, [exceptions, selectedIds]);
    const handleRowSelectAll = useCallback((checked) => {
        if (!checked) {
            setSelectedIds([]);
            return;
        }
        const unprocessed = exceptions.filter((acc) => !checkProcessedAccessorial(acc));
        setSelectedIds(unprocessed.map((acc) => acc._id));
    }, [exceptions]);
    const handleAccessorialRemove = useCallback((accessorialId) => {
        setSelectedIds((prev) => prev.filter((id) => id !== accessorialId));
    }, []);
    // -----------------------------------------------------------------------
    // Bulk actions
    // -----------------------------------------------------------------------
    const handleBulkAction = useCallback((bulkAction) => {
        let next = '';
        switch (bulkAction) {
            case 'Edit':
                next = 'edit';
                break;
            case 'Decline':
                next = 'decline_all';
                break;
            case 'Approve':
                next = 'approve_all';
                break;
            case 'Notify Shipper':
                next = 'notify_all';
                break;
            default:
                break;
        }
        setAction(next);
        setOpenBulkAssignModal(next !== 'edit');
    }, []);
    const handleAccessorialOnSubmit = useCallback((payload) => {
        switch (action) {
            case 'notify_all':
                AccessorialRequestActionCreators.updateAccessorialRequest(payload);
                break;
            case 'approve_all':
                AccessorialRequestActionCreators.approveAccessorials(payload);
                break;
            case 'decline_all':
                AccessorialRequestActionCreators.declineAccessorials(payload);
                break;
            default:
                return;
        }
        setAction('');
        setSelectedIds([]);
        setOpenBulkAssignModal(false);
    }, [action]);
    // -----------------------------------------------------------------------
    // Pane / Modal
    // -----------------------------------------------------------------------
    const hideShipmentModal = useCallback(() => {
        shouldPollRef.current = true;
        setShipmentModalId(null);
    }, []);
    const hidePane = useCallback(() => {
        setPaneAccessorialId(null);
        setPaneShipmentId(null);
    }, []);
    // -----------------------------------------------------------------------
    // CSV export
    // -----------------------------------------------------------------------
    const exportAccessorialsToCSV = useCallback(async (columns) => {
        try {
            setExportCSV((p) => !p);
            const query = buildAccessorialQuery({
                page: 1,
                paginate: false,
                per_page: 5000,
            });
            const { entity } = await downloadAccessorialExceptionsCSV(columns, query);
            const file = new Blob([entity], { type: 'text/csv' });
            const fileURL = URL.createObjectURL(file);
            const link = document.createElement('a');
            link.href = fileURL;
            link.download = 'accessorials_exceptions.csv';
            link.click();
            setExportCSV((p) => !p);
        }
        catch (_a) {
            showToast(t('CSV_DOWNLOAD_FAILED'), t('CSV_DOWNLOAD_FAILED_ACCESSORIAL'), 'error');
            setExportCSV((p) => !p);
        }
    }, [buildAccessorialQuery, t]);
    // -----------------------------------------------------------------------
    // Reset / handlers wiring
    // -----------------------------------------------------------------------
    const onReset = useCallback(() => {
        // Legacy parity: reset clears statuses to truly empty (not their initial
        // ['pending-admin'] default). Build a one-shot "reset" map and apply via
        // setMany — bypasses resetFilterState's restore-to-initial behavior.
        const cleared = {};
        for (const cfg of FILTERS) {
            cleared[cfg.id] =
                cfg.kind === 'select-multi' || cfg.kind === 'autocomplete-multi'
                    ? []
                    : cfg.kind === 'toggle'
                        ? false
                        : null;
        }
        setMany(cleared);
        setActivePage(1);
    }, [FILTERS, setMany]);
    const onChangeFilter = useCallback((id, value) => {
        setValue(id, value);
        setActivePage(1);
    }, [setValue]);
    const onToggleFilter = useCallback((id) => {
        toggle(id);
        setActivePage(1);
    }, [toggle]);
    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------
    if (!user)
        return React.createElement("div", null, t('LOADING'));
    const selectedAccessorials = exceptions.filter((s) => selectedIds.includes(s._id));
    const autoAppliedNeedsReviewAccessorialPresent = checkIfAccessorialsNeedsReview(selectedAccessorials);
    const missingEquipment = checkIfEquipmentIsMissing(selectedAccessorials);
    return (React.createElement(Container, { className: "py-6", useMaxWidth: false, dataTestId: "accessorial-exceptions" },
        React.createElement("div", null,
            action === 'edit' && (React.createElement(BulkAddAccessorialsModal, { show: true, shipmentIds: selectedAccessorials.map((acc) => {
                    var _a;
                    return typeof acc.shipment === 'string'
                        ? acc.shipment
                        : (_a = acc.shipment) === null || _a === void 0 ? void 0 : _a._id;
                }), onHide: () => setAction(''), edit: true, selectedAccessorials: selectedIds })),
            openBulkAssignModal && selectedIds.length > 0 && (React.createElement(AccessorialsConfirmationModal, { show: true, action: action, onHide: () => {
                    setAction('');
                    setOpenBulkAssignModal(false);
                }, selectedAccessorials: selectedIds, onAccessorialSubmit: handleAccessorialOnSubmit, handleAccessorialRemove: handleAccessorialRemove })),
            shipmentModalId &&
                // CargomaticModal has loose JS propTypes; cast to bypass strict TS shape
                React.createElement(CargomaticModal, {
                    show: true,
                    onHide: hideShipmentModal,
                    title: titleize(t('DETAIL', { count: 2 })),
                    enforceFocus: false,
                    body: React.createElement(ShipmentModule, { modalID: shipmentModalId, dontRefresh: true }),
                    footer: null,
                    location: 'top',
                    dialogClassName: 'modal-override',
                }),
            paneAccessorialId && (React.createElement("div", null,
                React.createElement("div", { id: "pane-container" }),
                React.createElement(CargomaticPane, {
                    show: true,
                    title: t('ACCESSORIAL_REQUESTS'),
                    onHide: hidePane,
                    renderBody: () => React.createElement(AccessorialRequestPane, {
                        accessorial: paneAccessorialId,
                        shipment: paneShipmentId,
                        showAccessorialRequestPane: true,
                        onSave: updateAccessorialsView,
                    }),
                    footer: null,
                    maskClosable: false,
                    location: 'right',
                }))),
            React.createElement(Card, null,
                React.createElement(FilterCardHeader, { title: t('ACCESSORIAL_REQUESTS'), onReset: onReset, actions: React.createElement(React.Fragment, null,
                        React.createElement(ExportAccessorialExceptionCSV, { exportAccessorialsToCSV: exportAccessorialsToCSV, exportCSV: exportCSV }),
                        React.createElement(DropdownMenu, null,
                            React.createElement(DropdownMenuTrigger, { asChild: true },
                                React.createElement(UIButton, { variant: "outline", disabled: !selectedIds.length },
                                    t('BULK_ACTIONS'),
                                    React.createElement(ChevronDown, { className: "h-4 w-4 ml-1" }))),
                            React.createElement(DropdownMenuContent, { align: "end" },
                                React.createElement(DropdownMenuItem, { disabled: !SessionStore.isAdmin(), onClick: () => handleBulkAction('Edit') }, titleize(t('EDIT_ACCESSORIAL'))),
                                React.createElement(DropdownMenuItem, { onClick: () => handleBulkAction('Decline') }, titleize(t('DECLINE'))),
                                React.createElement(DropdownMenuItem, { disabled: autoAppliedNeedsReviewAccessorialPresent ||
                                        missingEquipment, onClick: () => handleBulkAction('Approve') }, titleize(t('APPROVE'))),
                                React.createElement(DropdownMenuItem, { disabled: autoAppliedNeedsReviewAccessorialPresent ||
                                        missingEquipment, onClick: () => handleBulkAction('Notify Shipper') }, t('NOTIFY_SHIPPER'))))) },
                    React.createElement(FilterBarRegistry, { configs: FILTERS, values: values, labels: labels, displayed: displayed, onChange: onChangeFilter, onLabelChange: (id, optionId, label) => setLabel(id, optionId, label), onToggle: onToggleFilter })),
                React.createElement(CardContent, null,
                    React.createElement(TableSelectionAlert, { selectedCount: selectedIds.length, totalCount: totalCount, onClearSelection: () => setSelectedIds([]) },
                        autoAppliedNeedsReviewAccessorialPresent && (React.createElement(CargomaticAlert, { dataTestId: "Alert-Auto-Apply-Bulk-Accessorial-Exception", className: "mb-0", type: "warning" }, t('AUTO_APPLY_BULK_ACCESSORIAL_WARNING_MESSAGE'))),
                        missingEquipment && (React.createElement(CargomaticAlert, { dataTestId: "Alert-Equipment-Bulk-Accessorial-Exception", className: "mb-0", type: "error" }, t('NOT_APPLIED_TO_AN_EQUIPMENT')))),
                    React.createElement(AccessorialExceptionsTable, { data: exceptions, loading: loading, selectedIds: selectedIds, onSelect: handleRowSelect, onSelectAll: handleRowSelectAll, page: activePage, pages: pages, total: totalCount, perPage: perPage, onPageChange: (p) => setActivePage(p), pageSizeOptions: [25, 50, 100], onPageSizeChange: (size) => {
                            setPerPage(size);
                            setActivePage(1);
                        }, sortField: "approval_due_date", sortOrder: sortOrder, onSortChange: (_field, order) => {
                            setSortOrder(order);
                            setActivePage(1);
                        }, onShowShipmentModal: (id) => {
                            shouldPollRef.current = false;
                            setShipmentModalId(id);
                        }, onShowAccessorialRequestPane: (acc) => {
                            var _a;
                            const shipmentId = typeof acc.shipment === 'string'
                                ? acc.shipment
                                : (_a = acc.shipment) === null || _a === void 0 ? void 0 : _a._id;
                            setPaneAccessorialId(acc._id);
                            setPaneShipmentId(shipmentId !== null && shipmentId !== void 0 ? shipmentId : null);
                        }, emptyText: loading
                            ? t('LOADING_ACCESSORIALS')
                            : t('ACCESSORIAL_REQUESTS_NOT_FOUND') }))))));
}
export const AccessorialExceptions = withTranslation()(AccessorialExceptionsBase);
export default AccessorialExceptions;
